import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import { useToast } from '../hooks/use-toast';
import { ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import { track, events } from '../lib/analytics';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const REQUEST_OPTIONS = [
  { value: 'opt-out', label: 'Do not sell or share my personal information' },
  { value: 'delete', label: 'Delete my personal information' },
  { value: 'access', label: 'Send me a copy of the data you hold about me' },
  { value: 'correct', label: 'Correct information you hold about me' },
];

const Field = ({ label, active, children }) => (
  <label className="block">
    <span
      className={`block text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
        active ? 'text-brand-blue' : 'text-body-muted'
      }`}
    >
      {label}
    </span>
    {children}
  </label>
);

const DoNotSell = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    requestType: REQUEST_OPTIONS[0].value,
    residency: '',
    details: '',
  });
  const [focus, setFocus] = useState({ name: false, email: false, residency: false, details: false });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const inputCls = (active) =>
    `mt-1 w-full h-11 bg-transparent border-b-2 text-[15px] text-navy placeholder:text-body-muted focus:outline-none transition-colors ${
      active ? 'border-brand-blue' : 'border-slate-200'
    }`;

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    setErrorMsg('');
    if (!name || !email) {
      toast({ title: 'Please enter your name and email' });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast({ title: 'Please enter a valid email' });
      return;
    }
    const requestLabel =
      REQUEST_OPTIONS.find((o) => o.value === form.requestType)?.label || form.requestType;
    const message = [
      `Request type: ${requestLabel}`,
      form.residency ? `State / country: ${form.residency}` : '',
      form.details ? `Additional details:\n${form.details}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');

    setSubmitting(true);
    setStatus('idle');
    try {
      await axios.post(`${API_BASE}/leads`, {
        name,
        email,
        source: 'do-not-sell',
        gdpr_consent: false,
        message,
      });
      track(events.PRIVACY_REQUEST_SUBMIT, { request_type: form.requestType });
      setForm({ name: '', email: '', requestType: REQUEST_OPTIONS[0].value, residency: '', details: '' });
      setStatus('success');
      toast({
        title: 'Request received',
        description: 'We will process your request within 15 business days.',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const description =
        typeof detail === 'string'
          ? detail
          : 'Please try again in a moment or email connect@fivedbs.com directly.';
      setStatus('error');
      setErrorMsg(description);
      toast({ title: 'Something went wrong', description });
    } finally {
      setSubmitting(false);
    }
  };

  const submitAnother = () => {
    setStatus('idle');
    setErrorMsg('');
  };

  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">Privacy Rights</p>
          <h1 className="text-navy font-black tracking-[-0.01em] leading-[1.08] text-[36px] sm:text-[44px]">
            Do Not Sell or Share My Personal Information
          </h1>
          <p className="mt-4 text-body-muted text-[14px]">Last updated: 17 August 2026</p>

          {/* Position statement */}
          <div className="mt-10 rounded-2xl bg-slate-50 border border-slate-200 p-6 sm:p-8 flex items-start gap-4">
            <span className="mt-1 h-10 w-10 rounded-lg bg-brand-blue/10 border border-brand-blue/30 inline-flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-brand-blue" />
            </span>
            <div>
              <p className="text-navy font-bold text-[16px]">5DBS does not sell your personal information.</p>
              <p className="mt-2 text-body-muted text-[14.5px] leading-[1.7]">
                We are a business-to-business consultancy. We do not sell personal information in exchange for money,
                and we do not share personal information with third parties for cross-context behavioural advertising.
              </p>
              <p className="mt-3 text-body-muted text-[14.5px] leading-[1.7]">
                California and other US state privacy laws use a broad definition of &ldquo;selling&rdquo; and &ldquo;sharing&rdquo;
                that can include analytics cookies and marketing pixels. If you would still like us to formally record
                an opt-out or exercise any of your other privacy rights, please use the form below.
              </p>
            </div>
          </div>

          {/* Your rights */}
          <section className="mt-12">
            <h2 className="text-navy text-[22px] font-black tracking-tight">Your rights</h2>
            <p className="mt-3 text-body-muted text-[15px] leading-[1.75]">
              Depending on where you live, you may have the following rights over your personal information:
            </p>
            <ul className="mt-4 space-y-2.5 text-body-muted text-[15px] leading-[1.75] list-disc pl-5">
              <li><strong className="text-navy">Right to opt out</strong> of the sale or sharing of your personal information for cross-context behavioural advertising.</li>
              <li><strong className="text-navy">Right to delete</strong> personal information we have collected about you.</li>
              <li><strong className="text-navy">Right to access</strong> a copy of the personal information we hold about you.</li>
              <li><strong className="text-navy">Right to correct</strong> inaccurate personal information.</li>
              <li><strong className="text-navy">Right to non-discrimination</strong> &mdash; we will not deny services, charge different prices, or provide a different level of quality because you exercised any of these rights.</li>
              <li><strong className="text-navy">Right to appeal</strong> a decision we make about your request. If we decline, we will tell you why and how to appeal.</li>
            </ul>
            <p className="mt-4 text-body-muted text-[14px] leading-[1.7]">
              For full details, please see our <Link to="/privacy" className="text-brand-blue font-semibold hover:underline">Privacy Policy</Link>.
            </p>
          </section>

          {/* Authorised agents */}
          <section className="mt-12">
            <h2 className="text-navy text-[22px] font-black tracking-tight">Authorised agents</h2>
            <p className="mt-3 text-body-muted text-[15px] leading-[1.75]">
              You may designate an authorised agent to submit a request on your behalf. We will require written
              proof that you have granted them permission to act for you, and we may contact you directly to verify
              the request.
            </p>
          </section>

          {/* Request form */}
          <section id="request" className="mt-14">
            <h2 className="text-navy text-[22px] font-black tracking-tight">Submit a privacy request</h2>
            <p className="mt-3 text-body-muted text-[15px] leading-[1.7]">
              We aim to acknowledge every request within 10 business days and complete it within 45 days (or the
              period required by the law that applies to you).
            </p>

            <div className="mt-6 rounded-2xl bg-white border border-slate-200 shadow-mk p-6 sm:p-8">
              {status === 'success' ? (
                <div
                  role="status"
                  data-testid="dns-success-panel"
                  className="rounded-xl border border-emerald-200 bg-emerald-50 p-6"
                >
                  <div className="flex items-start gap-4">
                    <span className="mt-0.5 h-10 w-10 rounded-full bg-emerald-500/10 border border-emerald-500/30 inline-flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-emerald-800 text-[11px] font-bold uppercase tracking-[0.16em]">Request received</p>
                      <h3 className="mt-1 text-navy font-black text-[20px] leading-tight">Thanks &mdash; we&apos;re on it.</h3>
                      <p className="mt-2 text-body-muted text-[14.5px] leading-[1.65]">
                        A member of the 5DBS privacy team will acknowledge your request within 10 business days.
                        If we need any additional information to verify your identity, we will contact you at the email you provided.
                      </p>
                      <div className="mt-5 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          data-testid="dns-submit-another-button"
                          onClick={submitAnother}
                          className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300"
                        >
                          Submit another request
                        </button>
                        <Link
                          to="/privacy"
                          className="inline-flex items-center justify-center h-10 px-5 rounded-md border border-slate-300 text-navy hover:border-brand-blue hover:text-brand-blue text-[12px] font-bold uppercase tracking-[0.08em] transition-colors"
                        >
                          Read Privacy Policy
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="dns-form">
                  {status === 'error' && (
                    <div
                      role="alert"
                      data-testid="dns-error-panel"
                      className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3"
                    >
                      <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-rose-800 text-[11px] font-bold uppercase tracking-[0.16em]">Request not sent</p>
                        <p className="mt-1 text-navy text-[14px] leading-[1.55]">{errorMsg}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Your full name" active={focus.name}>
                      <input
                        placeholder="Alex Doe"
                        data-testid="dns-name-input"
                        value={form.name}
                        onFocus={() => setFocus((f) => ({ ...f, name: true }))}
                        onBlur={() => setFocus((f) => ({ ...f, name: false }))}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        className={inputCls(focus.name)}
                      />
                    </Field>
                    <Field label="Email we can reach you at" active={focus.email}>
                      <input
                        placeholder="you@company.com"
                        type="email"
                        data-testid="dns-email-input"
                        value={form.email}
                        onFocus={() => setFocus((f) => ({ ...f, email: true }))}
                        onBlur={() => setFocus((f) => ({ ...f, email: false }))}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        className={inputCls(focus.email)}
                      />
                    </Field>
                  </div>

                  <div>
                    <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted mb-3">
                      What would you like us to do?
                    </span>
                    <div className="grid grid-cols-1 gap-2">
                      {REQUEST_OPTIONS.map((opt) => {
                        const active = form.requestType === opt.value;
                        return (
                          <label
                            key={opt.value}
                            className={`flex items-start gap-3 rounded-lg border p-3.5 cursor-pointer transition-colors ${
                              active
                                ? 'border-brand-blue bg-brand-blue/5'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="requestType"
                              value={opt.value}
                              checked={active}
                              onChange={() => setForm((f) => ({ ...f, requestType: opt.value }))}
                              data-testid={`dns-request-${opt.value}`}
                              className="mt-0.5 h-4 w-4 text-brand-blue focus:ring-brand-blue cursor-pointer shrink-0"
                            />
                            <span className="text-navy text-[14.5px] leading-[1.5]">{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <Field label="State or country of residence (optional)" active={focus.residency}>
                    <input
                      placeholder="e.g. California, USA"
                      data-testid="dns-residency-input"
                      value={form.residency}
                      onFocus={() => setFocus((f) => ({ ...f, residency: true }))}
                      onBlur={() => setFocus((f) => ({ ...f, residency: false }))}
                      onChange={(e) => setForm((f) => ({ ...f, residency: e.target.value }))}
                      className={inputCls(focus.residency)}
                    />
                  </Field>

                  <Field label="Anything else we should know? (optional)" active={focus.details}>
                    <textarea
                      rows={4}
                      placeholder="Add details that will help us verify or complete your request."
                      data-testid="dns-details-input"
                      value={form.details}
                      onFocus={() => setFocus((f) => ({ ...f, details: true }))}
                      onBlur={() => setFocus((f) => ({ ...f, details: false }))}
                      onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
                      className={`mt-2 w-full bg-transparent border-b-2 py-2 text-[15px] text-navy placeholder:text-body-muted focus:outline-none transition-colors resize-none ${
                        focus.details ? 'border-brand-blue' : 'border-slate-200'
                      }`}
                    />
                  </Field>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-body-muted text-[12.5px] leading-[1.55] max-w-md">
                      We may contact you to verify your identity before completing the request, as required by law.
                      See our{' '}
                      <Link to="/privacy" className="text-brand-blue font-semibold hover:underline">
                        Privacy Policy
                      </Link>.
                    </p>
                    <button
                      type="submit"
                      data-testid="dns-submit-button"
                      disabled={submitting}
                      className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 shrink-0"
                    >
                      {submitting ? 'Submitting…' : 'Submit request'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <p className="mt-6 text-body-muted text-[13.5px] leading-[1.7]">
              Prefer email? Send your request to{' '}
              <a href="mailto:connect@fivedbs.com" className="text-brand-blue font-semibold hover:underline">
                connect@fivedbs.com
              </a>{' '}
              with the subject line &ldquo;Privacy Request&rdquo;.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DoNotSell;
